import React, { useState, useEffect } from 'react';
import { Box, List, ListItem, ListItemButton, ListItemText, Typography, Collapse } from '@mui/material';
import { styled } from '@mui/material/styles';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { getSectors, hasSubSectors } from '../utils/sectorUtils';

const SidebarContainer = styled(Box)(({ theme }) => ({
  position: 'sticky',
  top: '2rem',
  height: 'calc(100vh - 4rem)',
  overflowY: 'auto',
  backgroundColor: '#FFFFFF',
  borderRadius: '12px',
  border: '1px solid #E2E8F0',
  padding: '1.5rem'
}));

const SidebarTitle = styled(Typography)(({ theme }) => ({
  fontSize: '1.25rem',
  fontWeight: 600,
  color: '#1A365D',
  marginBottom: '1.5rem'
}));

const StyledListItem = styled(ListItem)(({ theme }) => ({
  padding: 0,
  marginBottom: '0.5rem'
}));

const StyledListItemButton = styled(ListItemButton)(({ theme, active }) => ({
  borderRadius: '8px',
  padding: '8px 12px',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'linear-gradient(120deg, transparent 0%, transparent 50%, #004B87 50%, #004B87 100%)',
    backgroundSize: '250%',
    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
  },
  '&.Mui-selected': {
    '&::before': {
      backgroundPosition: '100%',
    },
    '& .MuiListItemText-primary': {
      color: '#ffffff',
      position: 'relative',
    }
  },
  '&:hover': {
    '&::before': {
      backgroundPosition: '100%',
    },
    '& .MuiListItemText-primary': {
      color: '#ffffff',
      position: 'relative',
    }
  },
  '& .MuiListItemText-primary': {
    color: '#475569',
    fontWeight: active ? 600 : 400,
    fontSize: '0.95rem',
    transition: 'color 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
    position: 'relative',
  }
}));

const SubListItemButton = styled(ListItemButton)(({ theme, active }) => ({
  borderRadius: '8px',
  paddingLeft: '2rem',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'linear-gradient(120deg, transparent 0%, transparent 50%, #004B87 50%, #004B87 100%)',
    backgroundSize: '250%',
    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
  },
  '&.Mui-selected': {
    '&::before': {
      backgroundPosition: '100%',
    },
    '& .MuiListItemText-primary': {
      color: '#ffffff',
      position: 'relative',
    }
  },
  '&:hover': {
    '&::before': {
      backgroundPosition: '100%',
    },
    '& .MuiListItemText-primary': {
      color: '#ffffff',
      position: 'relative',
    }
  },
  '& .MuiListItemText-primary': {
    color: active ? '#475569' : '#64748B',
    fontWeight: active ? 500 : 400,
    fontSize: '0.9rem',
    transition: 'color 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
    position: 'relative',
  }
}));

const SectorSidebar = ({ company, activeSector, onSectorChange }) => {
  const sectors = getSectors(company);
  const [openSectors, setOpenSectors] = useState({});

  const scrollToContent = () => {
    const titleElement = document.querySelector('.sector-title-anchor');
    if (titleElement) {
      const offset = 150; // Augmentation de l'offset pour voir la carte entière
      const elementPosition = titleElement.getBoundingClientRect().top + window.pageYOffset - offset;
      window.scrollTo({
        top: elementPosition,
        behavior: 'smooth'
      });
    }
  };

  useEffect(() => {
    if (activeSector) {
      const parts = activeSector.split('-');
      const newOpenSectors = {};
      let currentPath = '';
      
      parts.forEach((part, index) => {
        currentPath = currentPath ? `${currentPath}-${part}` : part;
        newOpenSectors[currentPath] = true;
      });
      
      setOpenSectors(newOpenSectors);
    }
  }, [activeSector]);

  const handleSectorClick = (sectorId) => {
    const sector = findSectorById(sectors, sectorId);
    if (hasSubSectors(sector)) {
      setOpenSectors(prev => ({
        ...prev,
        [sectorId]: !prev[sectorId]
      }));
    } else {
      onSectorChange(sectorId);
      scrollToContent();
    }
  };

  const findSectorById = (sectorList, targetId) => {
    for (const sector of sectorList) {
      if (sector.id === targetId) {
        return sector;
      }
      if (sector.subSectors) {
        const found = findSectorById(sector.subSectors, targetId);
        if (found) return found;
      }
    }
    return null;
  };

  const renderSectors = (sectors, level = 1) => {
    return sectors.map((sector) => (
      <React.Fragment key={sector.id}>
        <StyledListItem disablePadding>
          <StyledListItemButton
            selected={activeSector === sector.id}
            onClick={() => handleSectorClick(sector.id)}
            sx={{ 
              pl: level * 2,
              '&.Mui-selected': {
                backgroundColor: '#E2E8F0',
              },
              '&.Mui-selected:hover': {
                backgroundColor: '#CBD5E1',
              }
            }}
          >
            <ListItemText 
              primary={sector.name}
              primaryTypographyProps={{
                sx: { 
                  color: '#475569',
                  fontWeight: activeSector === sector.id ? 600 : 400,
                  fontSize: level === 1 ? '0.95rem' : '0.9rem'
                }
              }}
            />
            {hasSubSectors(sector) && (
              openSectors[sector.id] ? <ExpandLess /> : <ExpandMore />
            )}
          </StyledListItemButton>
        </StyledListItem>
        {hasSubSectors(sector) && (
          <Collapse in={openSectors[sector.id]} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {renderSectors(sector.subSectors, level + 1)}
            </List>
          </Collapse>
        )}
      </React.Fragment>
    ));
  };

  return (
    <SidebarContainer>
      <SidebarTitle variant="h3">
        Nos Secteurs d'Activité
      </SidebarTitle>
      <List disablePadding>
        {renderSectors(sectors)}
      </List>
    </SidebarContainer>
  );
};

export default SectorSidebar;
